import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/store/use-ui-preferences-store";
import { CountrySelector } from "@/features/country-select";
import { useProfile } from "../hooks/use-profile";

/**
 * Contact Section
 * Location and phone for regional personalization
 */
export const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useProfile();
  const { language } = useLanguage();

  const getDefaultCountry = (lang: string): string => {
    const languageToCountry: Record<string, string> = {
      Turkish: "TR",
      English: "US",
      German: "DE",
      Arabic: "SA",
      Spanish: "ES",
      French: "FR",
  Italian: "IT",
    };
    return languageToCountry[lang] || "US";
  };

  return (
    <section className="flex flex-col gap-4 md:gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-text-primary dark:text-gray-100 leading-6">
          {t("settings.profile.contactInfo")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-2xl">
          {t("settings.profile.contactInfoDescription")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
        <div className="flex flex-col gap-1.5 rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800 p-3.5 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-4 w-4 text-text-brand dark:text-indigo-400 shrink-0" />
            <label className="text-sm font-medium text-text-primary dark:text-gray-200 leading-5">
              {t("settings.profile.country")}
            </label>
          </div>
          <CountrySelector
            id="country-selector"
            value={formData.region}
            onChange={(value) => updateFormData("region", value)}
            placeholder={t("settings.profile.countryPlaceholder")}
          />
        </div>

        <div className="flex flex-col gap-1.5 rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800 p-3.5 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-4 w-4 text-text-brand dark:text-indigo-400 shrink-0" />
            <label className="text-sm font-medium text-text-primary dark:text-gray-200 leading-5">
              {t("settings.profile.phoneNumber")}
            </label>
          </div>
          <div className="phone-input-wrapper flex items-center h-[2.75rem] min-h-[2.75rem] px-3.5 rounded-lg border border-border-primary dark:border-gray-600 bg-white dark:bg-gray-900 transition-all focus-within:ring-2 focus-within:ring-text-brand dark:focus-within:ring-indigo-400 focus-within:border-transparent">
            <PhoneInput
              international
              defaultCountry={getDefaultCountry(language) as any}
              value={formData.phoneNumber || undefined}
              onChange={(value) => updateFormData("phoneNumber", value || "")}
              placeholder={t("settings.profile.phoneNumberPlaceholder")}
              autoComplete="off"
              numberInputProps={{
                autoComplete: "off",
                name: "profile-phone-number",
              }}
              className="phone-input"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
